/**
 * Tenant Access Zone detail page (Tranche AF).
 *
 * Surfaces controller-vs-portal drift via the `/access-groups/:id/drift`
 * endpoint and offers three repair actions:
 *
 *   - Resync: enqueue a fresh `access_group_sync` outbox job.
 *   - Mark reconciled: operator asserts portal + controller agree.
 *   - Delete orphan controller object: only enabled when drift is
 *     `orphan_controller`.
 *
 * The drift banner and diff modal use the same Tailwind palette as the
 * rest of the portal — no new design system.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@ng-unifi/ui';
import { apiClient, type ApiRequestInit } from '../lib/api.js';
import { SyncStatusBadge, type SyncStatus } from '../components/SyncStatusBadge.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';

interface AccessGroupRow {
  id: string;
  name: string;
  schedule_id: string | null;
  schedule?: { id: string; name: string } | null;
  controller_enforced: boolean;
  sync_status: SyncStatus;
  last_sync_error: string | null;
  last_sync_at: string | null;
  last_reconciled_at: string | null;
  last_reconciled_reason: string | null;
  unifi_user_group_id: string | null;
  unifi_access_policy_id: string | null;
  created_at: string;
}

type DriftStatus =
  | 'in_sync'
  | 'pending'
  | 'in_flight'
  | 'failed'
  | 'drifted'
  | 'orphan_local'
  | 'orphan_controller';

interface DriftPayload {
  groupId: string;
  controllerPolicyId: string | null;
  controllerUserGroupId: string | null;
  status: DriftStatus;
  membership: { onlyInPortal: string[]; onlyInController: string[] };
  schedule: {
    portalScheduleId: string | null;
    controllerScheduleId: string | null;
    mismatch: boolean;
  };
  resources: {
    portalDoorIds: string[];
    controllerDoorIds: string[];
    mismatch: boolean;
  };
  lastSyncAt: string | null;
  lastSyncError: string | null;
  evidence: { fetchedAt: string };
}

interface BannerCopy {
  tone: string;
  title: string;
  detail: string;
}

const BANNER_COPY: Record<DriftStatus, BannerCopy> = {
  in_sync: {
    tone: 'bg-secondary-container border-secondary-fixed-dim text-on-secondary-container',
    title: 'In sync',
    detail: 'The controller and portal agree on membership, schedule, and resources.',
  },
  pending: {
    tone: 'bg-tertiary-fixed border-tertiary-fixed-dim text-on-tertiary-fixed',
    title: 'Sync pending',
    detail: 'A controller sync is queued. The worker will reconcile shortly.',
  },
  in_flight: {
    tone: 'bg-secondary-container border-secondary-fixed-dim text-on-secondary-container',
    title: 'Sync in flight',
    detail: 'The worker is currently writing changes to the controller.',
  },
  failed: {
    tone: 'bg-error-container border-error-container text-on-error-container',
    title: 'Sync failed',
    detail: 'The last sync attempt failed. Use Resync to retry, or Mark reconciled if you fixed it manually.',
  },
  drifted: {
    tone: 'bg-tertiary-fixed border-tertiary-fixed-dim text-on-tertiary-fixed',
    title: 'Drift detected',
    detail: 'The portal and controller disagree about this group. Use View diff to inspect, then Resync to reconcile.',
  },
  orphan_local: {
    tone: 'bg-tertiary-fixed border-tertiary-fixed-dim text-on-tertiary-fixed',
    title: 'Controller object missing',
    detail: 'The portal references a controller object that no longer exists. Resync will recreate it.',
  },
  orphan_controller: {
    tone: 'bg-error-container border-error-container text-on-error-container',
    title: 'Orphan controller object',
    detail: 'A controller-side user group or access policy is no longer referenced by the portal. Delete to clean up.',
  },
};

// Map-form lookup to satisfy the security/detect-object-injection lint rule.
const BANNER_LOOKUP = new Map<DriftStatus, BannerCopy>(
  Object.entries(BANNER_COPY) as [DriftStatus, BannerCopy][],
);

async function loadGroup(id: string, scopeOpts?: ApiRequestInit): Promise<AccessGroupRow> {
  const res = await apiClient.get<{ data: AccessGroupRow }>(
    `/api/v1/access-groups/${id}`,
    scopeOpts,
  );
  return res.data;
}

async function loadDrift(id: string, scopeOpts?: ApiRequestInit): Promise<DriftPayload> {
  const res = await apiClient.get<{ data: DriftPayload }>(
    `/api/v1/access-groups/${id}/drift`,
    scopeOpts,
  );
  return res.data;
}

export function AccessGroupDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const { scopeOpts } = useTenantScopedPage();
  const [group, setGroup] = useState<AccessGroupRow | null>(null);
  const [drift, setDrift] = useState<DriftPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    null | 'resync' | 'mark_reconciled' | 'delete_orphan_user_group' | 'delete_orphan_access_policy'
  >(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancel = false;
    async function go() {
      try {
        const [g, d] = await Promise.all([loadGroup(id, scopeOpts), loadDrift(id, scopeOpts)]);
        if (cancel) return;
        setGroup(g);
        setDrift(d);
      } catch (err) {
        if (!cancel) setError((err as Error).message);
      }
    }
    if (id) void go();
    return () => {
      cancel = true;
    };
  }, [id, scopeOpts]);

  async function refresh() {
    const [g, d] = await Promise.all([loadGroup(id, scopeOpts), loadDrift(id, scopeOpts)]);
    setGroup(g);
    setDrift(d);
  }

  async function doResync() {
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/access-groups/${id}/resync`, undefined, scopeOpts);
      await refresh();
      setConfirmAction(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doMarkReconciled() {
    if (reason.trim().length < 5) {
      setError('Reason must be at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/access-groups/${id}/mark-reconciled`, { reason }, scopeOpts);
      await refresh();
      setConfirmAction(null);
      setReason('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doDeleteOrphan(kind: 'user_group' | 'access_policy') {
    if (reason.trim().length < 5) {
      setError('Reason must be at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(
        `/api/v1/access-groups/${id}/delete-orphan-controller-object`,
        { reason, kind },
        scopeOpts,
      );
      await refresh();
      setConfirmAction(null);
      setReason('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !group) {
    return (
      <div className="p-6">
        <Link to="/access-groups" className="text-primary hover:underline">
          ← Back to Access Zones
        </Link>
        <div className="mt-4 rounded border border-error-container bg-error-container p-4 text-on-error-container">{error}</div>
      </div>
    );
  }
  if (!group || !drift) {
    return <div className="p-6 text-on-surface-variant">Loading…</div>;
  }

  const banner =
    BANNER_LOOKUP.get(drift.status) ?? {
      tone: 'bg-surface-container-low border-outline-variant text-on-surface',
      title: drift.status,
      detail: '',
    };
  const orphanControllerOnly = drift.status === 'orphan_controller';
  const hasUserGroupOrphan = orphanControllerOnly && group.unifi_user_group_id !== null;
  const hasAccessPolicyOrphan = orphanControllerOnly && group.unifi_access_policy_id !== null;
  const createdLabel = new Date(group.created_at).toLocaleString();
  const lastSyncedLabel = group.last_sync_at
    ? new Date(group.last_sync_at).toLocaleString()
    : 'Not synced yet';
  const portalScheduleLabel = group.schedule?.name ?? 'No schedule assigned';
  const controllerScheduleLabel = drift.schedule.controllerScheduleId
    ? 'Controller schedule present'
    : 'No controller schedule';

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/access-groups" className="text-sm text-primary hover:underline">
            ← Back to Access Zones
          </Link>
          <h1 className="mt-1 flex items-center gap-3 text-2xl font-semibold text-on-surface">
            {group.name}
            <SyncStatusBadge status={group.sync_status} />
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Created {createdLabel} · Last sync: {lastSyncedLabel}
          </p>
        </div>
      </div>

      {/* Drift banner */}
      <div className={`rounded border p-4 ${banner.tone}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold">{banner.title}</div>
            <div className="mt-1 text-sm">{banner.detail}</div>
            {drift.evidence.fetchedAt && (
              <div className="mt-2 text-xs opacity-75">
                Detected {new Date(drift.evidence.fetchedAt).toLocaleString()}
              </div>
            )}
            {drift.lastSyncError && (
              <div className="mt-2 rounded bg-surface-container-lowest/60 p-2 font-mono text-xs">
                {drift.lastSyncError}
              </div>
            )}
          </div>
          {drift.status !== 'in_sync' && (
            <button
              type="button"
              className="rounded border border-outline bg-surface-container-lowest px-3 py-1 text-xs text-on-surface-variant hover:bg-surface-container-low"
              onClick={() => setShowDiff(true)}
            >
              View diff
            </button>
          )}
        </div>
      </div>

      {/* Repair actions */}
      <div className="rounded border border-outline-variant bg-surface-container-lowest p-4">
        <h2 className="font-semibold text-on-surface">Repair actions</h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          All actions are audited. Mark reconciled and delete orphan require a reason.
          Building-owned doors are granted from Access Zones; this tenant view manages membership
          and controller sync inside those grants.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary disabled:opacity-50"
            onClick={() => setConfirmAction('resync')}
            disabled={busy}
          >
            Resync
          </button>
          <button
            type="button"
            className="rounded border border-outline bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface-variant hover:bg-surface-container-low disabled:opacity-50"
            onClick={() => setConfirmAction('mark_reconciled')}
            disabled={busy}
          >
            Mark reconciled
          </button>
          <button
            type="button"
            className="rounded border border-error bg-surface-container-lowest px-3 py-1.5 text-sm text-on-error-container hover:bg-error-container disabled:opacity-50"
            onClick={() => setConfirmAction('delete_orphan_user_group')}
            disabled={busy || !hasUserGroupOrphan}
            title={
              hasUserGroupOrphan
                ? 'Delete the orphan controller user group'
                : 'Only enabled when drift status is orphan_controller'
            }
          >
            Delete orphan user group
          </button>
          <button
            type="button"
            className="rounded border border-error bg-surface-container-lowest px-3 py-1.5 text-sm text-on-error-container hover:bg-error-container disabled:opacity-50"
            onClick={() => setConfirmAction('delete_orphan_access_policy')}
            disabled={busy || !hasAccessPolicyOrphan}
            title={
              hasAccessPolicyOrphan
                ? 'Delete the orphan controller access policy'
                : 'Only enabled when drift status is orphan_controller'
            }
          >
            Delete orphan access policy
          </button>
        </div>
        {group.last_reconciled_at && (
          <div className="mt-3 text-xs text-on-surface-variant">
            Last reconciled {new Date(group.last_reconciled_at).toLocaleString()} —{' '}
            {group.last_reconciled_reason}
          </div>
        )}
      </div>

      {/* Diff modal */}
      {showDiff && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-on-surface/40 p-margin-mobile">
          <div className="max-h-[calc(100dvh-2rem)] w-full max-w-3xl overflow-auto rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-title-lg text-title-lg font-semibold text-on-surface">
                Portal vs controller
              </h3>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container"
                onClick={() => setShowDiff(false)}
                aria-label="Close"
              >
                <Icon name="close" className="text-2xl" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <DiffPanel
                title="Membership"
                portal={drift.membership.onlyInPortal.length === 0 ? '(matches)' : drift.membership.onlyInPortal.join(', ')}
                controller={drift.membership.onlyInController.length === 0 ? '(matches)' : drift.membership.onlyInController.join(', ')}
              />
              <DiffPanel
                title="Schedule"
                portal={portalScheduleLabel}
                controller={controllerScheduleLabel}
              />
              <DiffPanel
                title="Doors"
                portal={drift.resources.portalDoorIds.join(', ') || '(none)'}
                controller={drift.resources.controllerDoorIds.join(', ') || '(none)'}
              />
              <DiffPanel
                title="Controller refs"
                portal={`user_group=${group.unifi_user_group_id ?? 'none'}\npolicy=${group.unifi_access_policy_id ?? 'none'}`}
                controller={`user_group=${drift.controllerUserGroupId ?? 'none'}\npolicy=${drift.controllerPolicyId ?? 'none'}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-on-surface/40 p-margin-mobile">
          <div className="w-full max-w-md rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-xl">
            <h3 className="font-title-lg text-title-lg font-semibold text-on-surface">
              {confirmAction === 'resync' && 'Confirm resync'}
              {confirmAction === 'mark_reconciled' && 'Confirm mark reconciled'}
              {confirmAction === 'delete_orphan_user_group' && 'Delete orphan controller user group'}
              {confirmAction === 'delete_orphan_access_policy' && 'Delete orphan controller access policy'}
            </h3>
            {confirmAction !== 'resync' && (
              <div className="mt-3">
                <label className="block font-label-md text-label-md text-on-surface-variant">
                  Reason (min 5 chars)
                </label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-outline-variant bg-surface p-2 text-body-md font-body-md text-on-surface"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-outline-variant bg-surface px-4 py-2.5 font-label-md text-label-md text-on-surface hover:bg-surface-container"
                onClick={() => {
                  setConfirmAction(null);
                  setReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2.5 font-label-md text-label-md text-on-primary hover:bg-primary-container disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  if (confirmAction === 'resync') void doResync();
                  else if (confirmAction === 'mark_reconciled') void doMarkReconciled();
                  else if (confirmAction === 'delete_orphan_user_group')
                    void doDeleteOrphan('user_group');
                  else if (confirmAction === 'delete_orphan_access_policy')
                    void doDeleteOrphan('access_policy');
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiffPanel({
  title,
  portal,
  controller,
}: {
  title: string;
  portal: string;
  controller: string;
}) {
  return (
    <div className="rounded-lg border border-outline-variant">
      <div className="border-b border-outline-variant bg-surface-container px-3 py-1 text-xs font-semibold text-on-surface-variant">
        {title}
      </div>
      <div className="grid grid-cols-1 divide-y divide-outline-variant sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-on-surface-variant">Portal</div>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-on-surface">
            {portal}
          </pre>
        </div>
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-on-surface-variant">Controller</div>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-on-surface">
            {controller}
          </pre>
        </div>
      </div>
    </div>
  );
}
